import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/components/auth/AuthProvider";
import { SecureStorageProvider } from "@/components/security/SecureStorage";
import { TeamProvider } from "@/components/team/TeamProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AppLoader } from "@/components/AppLoader";
import { AuthForm } from "@/components/auth/AuthForm";
import { AIAssistantProvider } from "@/contexts/AIAssistantContext";
import { AppShell } from "@/components/layout/AppShell";
import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";

// Lazy load all page components for better performance
const Index = lazy(() => import("./pages/Index"));
const Settings = lazy(() => import("./pages/Settings"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const TeamBusinessValuation = lazy(() => import("./pages/TeamBusinessValuation"));
const ValuationReport = lazy(() => import("./pages/ValuationReport"));
const BusinessReportPage = lazy(() => import("./pages/BusinessReportPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin"));
const BackfillActivityLogs = lazy(() => import("./pages/BackfillActivityLogs"));
const ChatTest = lazy(() => import("./pages/ChatTest"));
const ResearchTest = lazy(() => import("./pages/ResearchTest"));

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  // Only show loader on initial auth check, not on navigation
  if (loading && user === null) {
    return <AppLoader />;
  }

  // If user is not authenticated, show auth form
  if (!user) {
    return <AuthForm />;
  }

  return <AppShell>{children}</AppShell>;
};

const PrivateRouteWithTeams = ({ children }: { children: React.ReactNode }) => {
  const { user, teams, loading } = useAuth();

  // Only show loader on initial auth check, not on navigation
  if (loading && user === null) {
    return <AppLoader />;
  }

  // If user is not authenticated, show auth form
  if (!user) {
    return <AuthForm />;
  }

  // If user has no teams, show onboarding
  if (teams.length === 0) {
    return <AppShell><Onboarding /></AppShell>;
  }

  return <AppShell>{children}</AppShell>;
};

const AppRouter = () => {
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        {/* Public routes - no authentication required, no sidebar */}
        <Route path="/new" element={<TeamBusinessValuation />} />
        <Route path="/new/:teamSlug" element={<TeamBusinessValuation />} />

        {/* Private routes - authentication required, with sidebar */}
      <Route path="/" element={<PrivateRouteWithTeams><Index /></PrivateRouteWithTeams>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/settings/:section" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />
      <Route path="/business/:businessId" element={<PrivateRoute><BusinessReportPage /></PrivateRoute>} />
      <Route path="/valuation/:businessId/:valuationId" element={<PrivateRoute><ValuationReport /></PrivateRoute>} />
      <Route path="/payment-success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
      
      {/* Admin routes - admin authentication required, with sidebar */}
      <Route path="/admin" element={<AdminProtectedRoute><Admin /></AdminProtectedRoute>} />
      <Route path="/admin/backfill-logs" element={<AdminProtectedRoute><BackfillActivityLogs /></AdminProtectedRoute>} />

      {/* Test routes - for development/debugging */}
      <Route path="/test/chat" element={<PrivateRoute><ChatTest /></PrivateRoute>} />
      <Route path="/test/research" element={<PrivateRoute><ResearchTest /></PrivateRoute>} />

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <BrowserRouter>
        <TooltipProvider>
          <AuthProvider>
            <TeamProvider>
              <SecureStorageProvider>
                <Toaster />
                <Sonner />
                <AIAssistantProvider>
                  <AppRouter />
                </AIAssistantProvider>
              </SecureStorageProvider>
            </TeamProvider>
          </AuthProvider>
        </TooltipProvider>
      </BrowserRouter>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
